import { Component, ViewChild, ElementRef, inject } from '@angular/core';
import { takeUntil } from 'rxjs';

import { DataService } from '../data.service';
import { ImportService } from '../import.service';
import { Command, NavbarService } from '../navbar.service';
import * as XLSX from 'xlsx';
import { LatLng, LatLngBounds } from 'leaflet';
import { FilterService } from '../filter.service';
import { AuthService } from '../auth.service';
import { BohrpunktNeuComponent } from '../bohrpunkte/detail/bohrpunkt-neu.component';
import { ImportWizardComponent } from '../import/import-wizard.component';
import { Router } from '@angular/router';
import { AufnahmePunktDocument, bodenformLabelString, bodenformString, idOf, punktLabel, trackByIndex } from '../data-model';
import { formatBytes, LayerService, OfflineLayerService, OfflineDetailComponent, BaseMapListComponent } from '@luzula/luzula-lib';
import { latLngBounds } from 'leaflet';
import { ProjectService } from '../project.service';
import { DeleteConfirmationService } from '../shared/delete-confirmation/delete-confirmation.service';
import { firstValueFrom } from 'rxjs';

@Component({
    standalone: false,
    selector: 'bp-alle-bohrpunkte',
    templateUrl: './alle-bohrpunkte.component.html',
    styleUrls: ['./alle-bohrpunkte.component.css'],
})
export class AlleBohrpunkteComponent extends BaseMapListComponent<AufnahmePunktDocument> {
	private router = inject(Router);
	public ds = inject(DataService);
	public fs = inject(FilterService);
	public is = inject(ImportService);
	public ls = inject(LayerService);
	private ns = inject(NavbarService);
	public aus = inject(AuthService);
	public ols = inject(OfflineLayerService);
	public ps = inject(ProjectService);
	private deleteConfirmation = inject(DeleteConfirmationService);

	importDirectories = true;
	trackByIndex = trackByIndex;
	formatBytes = formatBytes;
	punktLabel = punktLabel;

	@ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;
	@ViewChild('reimportInput', { static: false }) reimportInput!: ElementRef<HTMLInputElement>;

	constructor() {
		super();
		this.navbarProvider.componentTitle = "Bohrpunkte";

		// Bei DB-Wechsel Punkte neu laden (falls nötig)
		let lastUserOfflineMode = this.oms.userOfflineMode();
		this.ds.currentDb_.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.ds.isInitialized) {
				const currentUserOfflineMode = this.oms.userOfflineMode();
				if (currentUserOfflineMode !== lastUserOfflineMode || !this.bps.length) {
					lastUserOfflineMode = currentUserOfflineMode;
					this.initPointsDebounced();
				}
			}
		});

		// Bei Offline-Modus-Wechsel Punkte neu laden
		this.oms.offlineModeChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.initPointsDebounced();
		});
	}

	// --- Abstract Method Implementations ---

	getPointLatLng(bp: AufnahmePunktDocument): LatLng | null {
		return this.ts.from(bp.header?.koordinaten);
	}

	matchesSearch(bp: AufnahmePunktDocument): boolean {
		if (!this.searchText || this.searchText.trim() === '') return true;

		const searchTerms = this.searchText.trim().split(/\s+/).filter(t => t.length > 0);
		if (searchTerms.length === 0) return true;

		// Case-insensitive Felder
		const label = this.punktLabel(bp, true)?.toLowerCase() || '';
		const nummer = bp.header?.nummer?.toLowerCase() || '';
		const forstamt = bp.header?.forstamt?.toLowerCase() || '';
		const revier = bp.header?.revier?.toLowerCase() || '';
		const kartierer = bp.header?.aufnahme?.kartierer?.toLowerCase() || '';
		const type = bp.type?.toLowerCase() || '';
		const datumRaw = bp.header?.aufnahme?.datum || '';
		const jahr = datumRaw ? new Date(datumRaw).getFullYear().toString() : '';
		const vegetation = bp.header?.vegetationsformengruppe?.toLowerCase() || '';

		// Case-sensitive Felder (FBF, HBF, HF)
		const bfArray = bp.header?.bodenform?.bf || [];
		const fbfValues = bfArray.map((b: any) => b.fbf || '').join(' ');
		const hbfValues = bfArray.map((b: any) => b.hbf || '').join(' ');
		const hf = bp.header?.humusform?.aufnahmeFlaeche?.neu || bp.header?.humusform?.aufnahmeFlaeche?.alt || '';

		return searchTerms.every(term => {
			const termLower = term.toLowerCase();
			const matchesInsensitive =
				label.includes(termLower) ||
				nummer.includes(termLower) ||
				forstamt.includes(termLower) ||
				revier.includes(termLower) ||
				kartierer.includes(termLower) ||
				type.includes(termLower) ||
				jahr.includes(termLower) ||
				vegetation.includes(termLower);
			const matchesSensitive =
				fbfValues.includes(term) ||
				hbfValues.includes(term) ||
				hf.includes(term);
			return matchesInsensitive || matchesSensitive;
		});
	}

	async initPoints(): Promise<void> {
		const currentVersion = ++this.initPointsVersion;
		try {
			this.splash.updateStatus('Auf Datenbank warten...');
			await this.ds.waitForInitialization();
			if (this.initPointsVersion !== currentVersion) return;
			this.splash.updateStatus('Punkte laden...');
			const result: any = await this.ds.queryIndex();
			if (this.initPointsVersion !== currentVersion) return;
			if (result === null) {
				console.log('initPoints: keine DB verfügbar, behalte bestehende Punkte');
				return;
			}
			const rows: any[] = result?.rows;
			if (!rows) return;

			const points = new Array<LatLng>();
			const bps = new Array<any>();
			const pointsFilter = new Array<boolean>;

			rows.forEach((bp: any) => {
				const mesoklima = bp.key[7];
				const stfgData = bp.key[8];
				// View-Daten direkt verwenden — Migrationen/Transformationen nicht nötig,
				// da die View die Daten schon im aktuellen Format ausgibt
				const extendedHeader: any = (mesoklima || stfgData) ? { mesoklima: mesoklima || undefined, stfg: stfgData ? { naehrkraftStufe: stfgData.nk, stammFeuchteStufe: { first: stfgData.sf1, second: stfgData.sf } } : undefined } : undefined;
				const _bp: any = {_id: bp.id, type: bp.key[1], header: bp.key[4], state: bp.key[5], version: bp.key[6], extendedHeader};
				points.push(this.ts.from(_bp.header.koordinaten));
				bps.push(_bp);
				pointsFilter.push(this.fs.filteredDocIds.has(bp.id));
			});

			if (this.initPointsVersion !== currentVersion) return;
			this.bps = bps;
			this.invalidateMapMarkersCache();
			this.points = points;
			this.pointsFilter = pointsFilter;
			this.filteredCount = pointsFilter.filter(f => f).length;
			this.invalidateSortCache();
			this.rebuildSortCache();
			this.updateBoundsFilter();
			this.splash.hide();
		} catch (reason: any) {
			this.msg.error(`Eine Verbindung zur Datenbank konnte nicht hergestellt werden (${reason.message})`, 'Fataler Fehler!', { disableTimeOut: true, closeButton: true });
			console.error(reason);
			this.splash.hide();
		}
	}

	initButtons(): void {
		this.ns.subscribe(Command.discard, () => this.onDiscard());
		if (this.ps.canI('checkin', 'points')) {
			this.ns.subscribe(Command.checkIn, () => this.onCheckin());
		} else {
			this.ns.unsubscribe(Command.checkIn);
		}

		if (this.ps.canI('checkout', 'points')) {
			this.ns.subscribe(Command.checkOut, () => this.onCheckout());
			this.ns.subscribe(Command.checkOutEmpty, () => this.onCheckoutEmpty());
		} else {
			this.ns.unsubscribe(Command.checkOut);
			this.ns.unsubscribe(Command.checkOutEmpty);
		}

		if (this.ps.canI('read', 'points')) {
			this.ns.subscribe(Command.export, () => this.onExport());
			this.ns.subscribe(Command.backupData, () => this.onBackupData());
			this.ns.subscribe(Command.exportPdf, () => this.onExportPdf());
		} else {
			this.ns.unsubscribe(Command.export);
			this.ns.unsubscribe(Command.backupData);
			this.ns.unsubscribe(Command.exportPdf);
		}

		if (this.ps.canIadd()) {
			this.ns.subscribe(Command.addPoint, (data?: any) => this.onAddBohrpunkt(data));
		} else {
			this.ns.subscribe(Command.addPoint, () => {
				this.msg.warning('Sie haben keine Berechtigung, Punkte in diesem Projekt anzulegen.');
			});
		}

		if (this.ps.canIremove()) {
			this.ns.subscribe(Command.deleteSelection, () => this.deleteSelection());
		} else {
			this.ns.unsubscribe(Command.deleteSelection);
		}

		if (this.ps.canAssignToProject()) {
			this.ns.subscribe(Command.assignSelectionToProject, () => this.assignSelectionToProject());
			this.ns.subscribe(Command.removeSelectionFromProject, () => this.removeSelectionFromProject());
		} else {
			this.ns.unsubscribe(Command.assignSelectionToProject);
			this.ns.unsubscribe(Command.removeSelectionFromProject);
		}

		if (this.ps.canIimport()) {
			this.ns.subscribe(Command.importDirectories, () => this.onImportDirectories());
			this.ns.subscribe(Command.importFiles, () => this.onImportFiles());
			this.ns.subscribe(Command.importWizard, () => this.onImportWizard());
			this.ns.subscribe(Command.reimportProtocols, () => this.onReimportProtocols());
		} else {
			this.ns.unsubscribe(Command.importDirectories);
			this.ns.unsubscribe(Command.importFiles);
			this.ns.unsubscribe(Command.importWizard);
			this.ns.unsubscribe(Command.reimportProtocols);
		}

		if (this.ps.amI('entwickler')) {
			this.ns.subscribe(Command.cleanUp, () => this.cleanUp());
		}
	}

	getSubscribedCommands(): number[] {
		return [
			Command.checkIn, Command.checkOut, Command.checkOutEmpty, Command.discard,
			Command.importDirectories, Command.importFiles, Command.importWizard, Command.reimportProtocols,
			Command.export, Command.backupData, Command.exportPdf,
			Command.addPoint, Command.deleteSelection,
			Command.assignSelectionToProject, Command.removeSelectionFromProject,
			Command.cleanUp
		];
	}

	// --- Domain-Specific Methods ---

	onFileAdded(event: any) {
		const target: DataTransfer = event.target as DataTransfer;
		const bpDocs = new Array<any>;

		let docsRead = 0, docsToRead = target.files.length;
		const maybeReady = (callback: any) => {
			if (++docsRead === docsToRead) callback();
		};
		let errorFile = '';
		const logError = (name: string) => {
			const error = `Fehler beim Lesen von ${name}`;
			this.msg.error(error);
			errorFile += error + '\n';
		};
		for (let idx = 0; idx < target.files.length; ++idx) {
			const file = target.files.item(idx);
			const reader: FileReader = new FileReader();
			reader.readAsArrayBuffer(file!);
			reader.onload = (e: any) => {
				const binarystr: string = e.target.result;
				const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: 'binary', cellDates: true });

				for (const sheetName of wb.SheetNames) {
					const ws: XLSX.WorkSheet = wb.Sheets[sheetName];

					if (!isNaN(parseInt(sheetName))) {
						const name = file?.webkitRelativePath && file.webkitRelativePath.length > 0 ? file.webkitRelativePath : file?.name;
						const nameWithSheet = `${name}[${sheetName}]`;
						try {
							const data = XLSX.utils.sheet_to_json(ws, { blankrows: true, header: "A" });
							const doc = this.is.bohrpunktFromExcelSheet(data);
							if (doc) bpDocs.push(doc);
							else {
								if (!name) console.error('error reading unknown file');
								logError(`${nameWithSheet}: Unbekanntes Format`);
							}
						}
						catch(err: any) {
							logError(`${nameWithSheet}: ${err.message}`);
						}
					}
				}
				maybeReady(() => {
					if (errorFile.length > 0) this.is.downloadTextFile('Fehlerprotokoll.txt', errorFile);
					this.ds.addBohrpunkte(bpDocs).then(result => {
						this.initPoints();
						console.log(result);
					}, error => console.error(error));
				});
			};
		}
	}

	private async onCheckout() {
		try {
			const pl = await this.storage.get('offlinePlusLevels');
			const st: any = await this.storage.get('MapStatus');
			const projectRegions = this.ls.regions()?.regions ?? [];
			const regionBounds = projectRegions.map(r => r.bounds);
			const regions = regionBounds.length > 0 ? regionBounds : (st?.bounds ? [new LatLngBounds(st.bounds._southWest, st.bounds._northEast)] : []);

			const modalRef = this.modalService.open(OfflineDetailComponent);
			modalRef.componentInstance.plusLevels = pl ? parseInt(pl) : 0;
			modalRef.componentInstance.regions = regions;

			const plusLevels: 0|1|2 = await firstValueFrom(modalRef.closed);
			await this.storage.set('offlinePlusLevels', plusLevels.toString());

			if (regions.length > 0) {
				this.ols.seedRequest.emit(plusLevels);
				const currentFilter = await this.storage.get('currentFilter');
				await this.ds.checkoutBohrpunkte(regions, currentFilter);
				this.fs.resetFilter();
				await this.oms.setOfflineMode(true);
				this.ls.offlineModeChanged.emit(true);
			}
		} catch (reason: any) {
			this.msg.error(reason.message);
		}
	}

	private async onCheckoutEmpty() {
		try {
			const pl = await this.storage.get('offlinePlusLevels');
			const st: any = await this.storage.get('MapStatus');
			const projectRegions = this.ls.regions()?.regions ?? [];
			const regionBounds = projectRegions.map(r => r.bounds);
			const regions = regionBounds.length > 0 ? regionBounds : (st?.bounds ? [new LatLngBounds(st.bounds._southWest, st.bounds._northEast)] : []);

			const modalRef = this.modalService.open(OfflineDetailComponent);
			modalRef.componentInstance.plusLevels = pl ? parseInt(pl) : 0;
			modalRef.componentInstance.regions = regions;

			const plusLevels: 0|1|2 = await firstValueFrom(modalRef.closed);
			await this.storage.set('offlinePlusLevels', plusLevels.toString());

			if (regions.length > 0) {
				this.ols.seedRequest.emit(plusLevels);
				await this.ds.checkoutEmpty();
				await this.oms.setOfflineMode(true);
				this.ls.offlineModeChanged.emit(true);
			}
		} catch (reason: any) {
			this.msg.error(reason.message);
		}
	}

	private async onCheckin() {
		const confirmed = await this.confirmService.confirm({
			title: 'Bohrpunkte synchronisieren',
			message: 'Sollen alle Bohrpunkte in die Online-Datenbank übernommen werden?\n\nSie bleiben im Offline-Modus und können weiterarbeiten.',
			confirmText: 'Synchronisieren',
			confirmClass: 'btn-success'
		});
		if (confirmed) {
			await this.ds.checkinDocuments();
			this.initPointsDebounced();
			this.msg.success('Bohrpunkte wurden synchronisiert');
		}
	}

	private async onDiscard() {
		const confirmed = await this.confirmService.confirmDiscard(
			'Sollen alle Änderungen an den offline verfügbar gemachten Bohrpunkten verworfen werden?'
		);
		if (confirmed) {
			await this.ds.discardLocal();
			await this.oms.setOfflineMode(false);
			this.ls.offlineModeChanged.emit(false);
			delete this.points;
		}
	}

	private onImportDirectories() {
		this.importDirectories = true;
		setTimeout(() => this.fileInput.nativeElement.click());
	}

	private onImportFiles() {
		this.importDirectories = false;
		setTimeout(() => this.fileInput.nativeElement.click());
	}

	private onImportWizard() {
		const modalRef = this.modalService.open(ImportWizardComponent, { size: 'lg' });
		modalRef.closed.subscribe((result: { imported: number, skipped: number }) => {
			if (result.imported > 0) {
				this.initPoints();
			}
		});
	}

	private onReimportProtocols() {
		setTimeout(() => this.reimportInput.nativeElement.click());
	}

	async onReimportFile(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		// Input zurücksetzen, damit dieselbe Datei erneut gewählt werden kann
		input.value = '';

		try {
			const text = await file.text();
			const data = JSON.parse(text);

			// Validierung: muss punkte-Array mit _id und header enthalten
			const punkte: any[] = data?.punkte;
			if (!Array.isArray(punkte) || punkte.length === 0) {
				this.msg.error('Die Datei enthält kein gültiges "punkte"-Array.', 'Ungültiges Format');
				return;
			}
			const invalid = punkte.filter(p => !p._id || !p.header);
			if (invalid.length > 0) {
				this.msg.error(`${invalid.length} Einträge ohne _id oder header gefunden.`, 'Ungültiges Format');
				return;
			}

			// Prüfen welche IDs schon in der DB existieren
			const ids = punkte.map(p => p._id);
			const existing = await this.ds.bulkGet(ids);
			const existingIds = new Set(existing.map((doc: any) => doc._id));

			const toImport = punkte
				.filter(p => !existingIds.has(p._id))
				.map(p => {
					const { _rev, ...rest } = p;
					return rest;
				});

			const skipped = punkte.length - toImport.length;

			if (toImport.length === 0) {
				this.msg.info(`Alle ${punkte.length} Punkte sind bereits in der Datenbank vorhanden.`);
				return;
			}

			await this.ds.addBohrpunkte(toImport);
			await this.initPoints();

			this.msg.success(
				`${toImport.length} Punkte importiert` + (skipped > 0 ? `, ${skipped} übersprungen (bereits vorhanden)` : ''),
				'Reimport abgeschlossen'
			);
		} catch (err: any) {
			this.msg.error(err.message || 'Unbekannter Fehler', 'Reimport fehlgeschlagen');
		}
	}

	private onExport() {
		this.onBackupData();
	}

	private getExportPoints(allRows: any[]): { points: any[], source: string } {
		if (this.selectionService.count() > 0) {
			const selectedIds = this.selectionService.getSelection();
			const points = allRows.filter((bp: any) => selectedIds.has(bp.doc._id));
			return { points, source: 'Auswahl' };
		}

		if (this.currentBounds) {
			let points = allRows.filter((bp: any) => {
				const latLngPoint = this.ts.from(bp.doc.header?.koordinaten);
				return latLngPoint ? this.currentBounds!.contains(latLngPoint) : false;
			});
			if (this.fs.currentFilterFunc && this.fs.filteredDocIds.size > 0) {
				points = points.filter((bp: any) => this.fs.filteredDocIds.has(bp.doc._id));
			}
			return { points, source: 'Kartenausschnitt' };
		}

		const points = this.fs.currentFilterFunc
			? allRows.filter((bp: any) => this.fs.filteredDocIds.has(bp.doc._id))
			: allRows;
		return { points, source: 'alle' };
	}

	private shouldMaskPersonalData(): boolean {
		return !this.aus.isAuthenticated.getValue() || this.aus.amI('gast');
	}

	private maskPointForExport(doc: any): any {
		if (!this.shouldMaskPersonalData()) return doc;
		const masked = JSON.parse(JSON.stringify(doc));
		if (masked.header?.aufnahme) {
			masked.header.aufnahme.kartierer = '–';
			masked.header.aufnahme.datum = null;
		}
		return masked;
	}

	private async onBackupData() {
		const response = await this.ds.queryIndex(true);
		if (!response) return;

		const { points, source } = this.getExportPoints(response.rows);
		if (points.length === 0) {
			this.msg.warning('Keine Punkte zum Exportieren gefunden');
			return;
		}

		// Dateinamen-Prefix: Projektname + Datum/Zeit
		const projectDoc = this.ps.currentProjectDoc();
		const projectName = (projectDoc?.name || 'Export').replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_');
		const now = new Date();
		const date = now.toISOString().slice(0, 10);
		const time = now.toTimeString().slice(0, 5).replace(':', '-');
		const prefix = `${projectName}_${date}_${time}`;

		const maskedPoints = points.map((bp: any) => this.maskPointForExport(bp.doc));

		// Alle Export-Dateien sammeln
		const files: {data: any, filename: string}[] = [];

		// 1. Protokolle (Sachdaten / Datenbankauszug)
		files.push({
			data: {
				exportDatum: now.toISOString(),
				quelle: source,
				anzahl: maskedPoints.length,
				punkte: maskedPoints
			},
			filename: `${prefix}_Protokolle.json`
		});

		// 2. Punkte als GeoJSON (Geodaten)
		files.push({ data: this.pointsToGeoJson(maskedPoints), filename: `${prefix}_Punkte.geojson` });

		// 3. Zeichnungen (Linien + Polygone)
		const zeichnungslayer = await this.ps.getProjectValue("zeichnungslayer") as any[] | null;
		let lineCount = 0;
		let polygonCount = 0;

		if (zeichnungslayer && zeichnungslayer.length > 0) {
			const exportBounds = this.getExportBounds(points);

			const allFeatures: any[] = [];
			for (const layer of zeichnungslayer) {
				if (layer.geojson?.features) {
					allFeatures.push(...layer.geojson.features);
				}
			}

			let filteredFeatures = allFeatures;
			if (exportBounds) {
				filteredFeatures = allFeatures.filter((feature: any) => {
					if (!feature.geometry?.coordinates) return false;
					return this.featureIntersectsBounds(feature, exportBounds);
				});
				if (filteredFeatures.length === 0) {
					filteredFeatures = allFeatures;
				}
			}

			const lineFeatures = filteredFeatures.filter((f: any) =>
				f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString'
			);
			const polygonFeatures = filteredFeatures.filter((f: any) =>
				f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
			);

			if (lineFeatures.length > 0) {
				files.push({ data: { type: 'FeatureCollection', features: lineFeatures }, filename: `${prefix}_Zeichnungen_Linien.geojson` });
				lineCount = lineFeatures.length;
			}
			if (polygonFeatures.length > 0) {
				files.push({ data: { type: 'FeatureCollection', features: polygonFeatures }, filename: `${prefix}_Zeichnungen_Polygone.geojson` });
				polygonCount = polygonFeatures.length;
			}
		}

		// Alle Dateien gebündelt exportieren (ein Ordner-Dialog)
		const success = await this.ls.saveMultipleJSONToFiles(files);

		if (success) {
			let msg = `Backup: ${maskedPoints.length} Protokolle + Punkte (${source})`;
			const zeichnungenCount = lineCount + polygonCount;
			if (zeichnungenCount > 0) {
				msg += `, ${zeichnungenCount} Zeichnungen`;
			}
			msg += ` — ${files.length} Dateien gespeichert`;
			this.msg.info(msg);
		}
	}

	private featureIntersectsBounds(feature: any, bounds: LatLngBounds): boolean {
		const coords = feature.geometry?.coordinates;
		if (!coords) return false;
		const checkCoord = (coord: number[]): boolean => coord.length >= 2 && bounds.contains([coord[1], coord[0]]);
		const checkCoords = (coordinates: any): boolean => {
			if (Array.isArray(coordinates[0]) && typeof coordinates[0][0] === 'number') {
				return coordinates.some((c: number[]) => checkCoord(c));
			} else if (Array.isArray(coordinates[0])) {
				return coordinates.some((ring: any) => checkCoords(ring));
			} else if (typeof coordinates[0] === 'number') {
				return checkCoord(coordinates);
			}
			return false;
		};
		return checkCoords(coords);
	}

	private onExportPdf() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.warning('Keine Punkte ausgewählt');
			return;
		}
		const ids = this.selectionService.getSelectionArray().join(',');
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids } });
	}

	private getExportBounds(points: any[]): LatLngBounds | undefined {
		if (this.selectionService.count() > 0 && points.length > 0) {
			let bounds: LatLngBounds | undefined;
			points.forEach((bp: any) => {
				const latLngPoint = this.ts.from(bp.doc.header?.koordinaten);
				if (latLngPoint) {
					bounds = bounds ? bounds.extend(latLngPoint) : latLngBounds(latLngPoint, latLngPoint);
				}
			});
			return bounds;
		}
		return this.currentBounds;
	}

	private async onAddBohrpunkt(data?: any) {
		if (!this.ps.project()) {
			this.msg.warning('Bitte zuerst ein Projekt öffnen.');
			return;
		}
		const modalRef = this.modalService.open(BohrpunktNeuComponent);
		modalRef.componentInstance.position = data;
		modalRef.closed.subscribe((value: any) => {
			const bp = value.bp;
			const redirect = value.redirect;
			this.ds.createBohrpunktFrom(bp)?.then((response: any) => {
				if (redirect) {
					this.router.navigate(['/bohrpunkte', response.id]);
				} else {
					this.initPointsDebounced();
				}
			});
		})
	}

	printSelection() {
		const count = this.selectionService.count();
		if (count === 0) return;
		const ids = this.selectionService.getSelectionArray().join(',');
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids } });
	}

	async deleteSelection() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		// Sammle die ausgewählten Punkte für die erweiterte Bestätigung
		const selectedPoints = this.bps.filter(bp => this.selectionService.isSelected(bp._id));

		const confirmed = await this.deleteConfirmation.confirmDeleteBulkFast(selectedPoints);
		if (!confirmed) return;

		const ids = this.selectionService.getSelectionArray();
		await this.ds.removeBohrpunkteBulk(ids);
		this.selectionService.clear();
		this.msg.success(`${count} Punkte wurden gelöscht`);
		this.initPoints();
	}

	async assignSelectionToProject() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) {
			this.msg.warning('Kein Projekt ausgewählt');
			return;
		}

		const ids = this.selectionService.getSelectionArray();
		const points = await this.ds.bulkGet(ids);

		const toSave: any[] = [];
		for (const bp of points) {
			if (!bp?.header) continue;
			if (!bp.header.projects) bp.header.projects = [];
			if (bp.header.projects.some((p: any) => p.id === projectDoc._id)) continue;
			bp.header.projects.push({ id: projectDoc._id, name: projectDoc.name });
			toSave.push(bp);
		}

		if (toSave.length === 0) {
			this.msg.info('Alle ausgewählten Punkte waren bereits dem Projekt zugeordnet');
			return;
		}

		await this.ds.bulkDocs(toSave);
		this.pps.invalidatePunkteCache();
		await this.pps.updateAssignedPoints();
		this.ls.assignedPointsChanged.emit(new Set(this.pps.assignedPointIds()));

		this.msg.success(`${toSave.length} Punkt${toSave.length > 1 ? 'e' : ''} dem Projekt zugeordnet`);
		this.selectionService.clear();
		this.initPoints();
	}

	async removeSelectionFromProject() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) {
			this.msg.warning('Kein Projekt ausgewählt');
			return;
		}

		const ids = this.selectionService.getSelectionArray();
		const points = await this.ds.bulkGet(ids);

		const toSave: any[] = [];
		for (const bp of points) {
			if (!bp?.header?.projects) continue;
			const idx = bp.header.projects.findIndex((p: any) => p.id === projectDoc._id);
			if (idx === -1) continue;
			bp.header.projects.splice(idx, 1);
			toSave.push(bp);
		}

		if (toSave.length === 0) {
			this.msg.info('Keine der ausgewählten Punkte war dem Projekt zugeordnet');
			return;
		}

		await this.ds.bulkDocs(toSave);
		this.pps.invalidatePunkteCache();
		await this.pps.updateAssignedPoints();
		this.ls.assignedPointsChanged.emit(new Set(this.pps.assignedPointIds()));

		this.msg.success(`${toSave.length} Punkt${toSave.length > 1 ? 'e' : ''} aus dem Projekt entfernt`);
		this.selectionService.clear();
		this.initPoints();
	}

	private pointsToGeoJson(bps: AufnahmePunktDocument[]) {
		const latLngToArray = (ll: LatLng) => [ll.lng, ll.lat];
		const hfOrVegFG = (bp: AufnahmePunktDocument) => {
			if (bp.header.humusform?.aufnahmeFlaeche) {
				if (bp.header.humusform.aufnahmeFlaeche.neu) return bp.header.humusform.aufnahmeFlaeche.neu;
				if (bp.header.humusform.aufnahmeFlaeche.alt) return bp.header.humusform.aufnahmeFlaeche.alt;
			}
			if (bp.header.vegetationsformengruppe) return bp.header.vegetationsformengruppe;
			return '';
		};
		return {
			type: 'FeatureCollection',
			features: bps.map((bp: AufnahmePunktDocument) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: latLngToArray(this.ts.from(bp.header.koordinaten))
				},
				properties: {
					SYM_TEXT: bp.header.nummer,
					SYMBOL: idOf(bp.type),
					HF: hfOrVegFG(bp),
					HBF: bodenformString(bp.header.bodenform, 'hbf'),
					BF: bodenformLabelString(bp),
					Bemerkungen: bp.header.bemerkungen
				}
			}))
		};
	}
}
